import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEmail } from '../../contexts/EmailContext';
import { useDemo } from '../../contexts/DemoContext';
import { ChevronLeft, ChevronRight, Settings, LogOut, Search, Sun, Moon } from 'lucide-react';
import AddClientModal from '../domains/advisor-portal/add-new-client/AddClientModal';
import { LayoutProvider, useLayout } from '../../contexts/LayoutContext';
import { LAYOUT } from '../../constants/layout';

function AppLayoutContent() {
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(true);
  const [expandedMenu, setExpandedMenu] = useState("");
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { isDemoMode } = useDemo();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { unreadCount } = useEmail();
  const { measureHeader, measureLeftSidebar, measureRightSidebar, rightSidebarWidth } = useLayout();
  const leftSidebarRef = useRef(null);

  // Add global click trap handler
  const handleGlobalClick = useCallback((e) => {
    if (!isDemoMode) return;

    // Only trap actual click events. Trapping pointer/mouse down/up can accidentally
    // kill legitimate clicks (and behaves differently across browsers/DevTools states).
    if (e.type !== 'click') return;

    // Normalize the target to an Element (some event targets can be non-Elements)
    const targetEl =
      e.target instanceof Element
        ? e.target
        : e.target?.parentElement instanceof Element
          ? e.target.parentElement
          : null;
    if (!targetEl) return;

    // Check if click target is explicitly allowed
    const isAllowedTarget = (el) => {
      const allowClick = el?.closest?.("[data-allow-demo-click='true']");
      const spotlightTarget = el?.closest?.("[data-spotlight-target]");
      const inModal = el?.closest?.(".demo-modal, [role='dialog']");

      // Support multiple concurrent spotlight targets (rare, but possible).
      const spotlightEls = Array.from(document.querySelectorAll('[data-spotlight-active="true"]'));
      const isSpotlightTarget =
        spotlightEls.length > 0 &&
        spotlightEls.some(spotlightEl => spotlightEl === el || spotlightEl.contains(el));

      // During demo, anything explicitly marked as spotlight-target is safe to click.
      return !!(allowClick || spotlightTarget || inModal || isSpotlightTarget);
    };

    // Log for debugging
    console.log('🎯 [AppLayout] Click:', {
      target: targetEl,
      isAllowed: isAllowedTarget(targetEl),
      path: e.composedPath?.()?.map(el => ({
        className: el.className,
        id: el.id,
        tagName: el.tagName,
        allowClick: el?.hasAttribute?.('data-allow-demo-click'),
        isModal: el?.matches?.('.demo-modal, [role="dialog"]'),
        isSpotlight: el?.hasAttribute?.('data-spotlight-active')
      }))
    });

    // Block all clicks except allowed targets during demo
    if (!isAllowedTarget(targetEl)) {
      console.log('🛑 [AppLayout] Blocked click on:', targetEl);
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      e.cancelBubble = true;
      e.returnValue = false;
      return false;
    }
  }, [isDemoMode]);

  // Add event listener
  useEffect(() => {
    if (isDemoMode) {
      document.addEventListener('click', handleGlobalClick, true);
    }
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [isDemoMode, handleGlobalClick]);

  // Measure after width transition (only once sidebar is done animating)
  useEffect(() => {
    const node = leftSidebarRef.current;
    if (!node) return;
    
    const handleTransitionEnd = (e) => {
      if (e.propertyName === "width") {
        measureLeftSidebar(node);
      }
    };
    
    node.addEventListener("transitionend", handleTransitionEnd);
    return () => node.removeEventListener("transitionend", handleTransitionEnd);
  }, [measureLeftSidebar, leftSidebarCollapsed]);

  // Always measure on mount and when collapsed state changes
  useEffect(() => {
    const node = leftSidebarRef.current;
    if (node) measureLeftSidebar(node);
  }, [measureLeftSidebar, leftSidebarCollapsed]);

  // Reset rightSidebarWidth when navigating to HomeDashboard
  useEffect(() => {
    if (location.pathname === "/home-dashboard") {
      measureRightSidebar(null); // Pass null to reset the measurement
    }
  }, [location.pathname, measureRightSidebar]);

  // Debug effect for layout measurements
  useEffect(() => {
    console.log('📏 [AppLayout] Layout measurements:', {
      leftSidebarCollapsed,
      rightSidebarWidth,
      windowWidth: window.innerWidth,
      availableSpace: window.innerWidth - (leftSidebarCollapsed ? 56 : 240) - rightSidebarWidth
    });
  }, [leftSidebarCollapsed, rightSidebarWidth]);

  // Get user info
  const advisorFirstName = user?.is_demo
    ? "Advisor"
    : user?.firstName ||
    JSON.parse(localStorage.getItem("user"))?.firstName ||
    "Advisor";

  const advisorInitial = user?.is_demo
    ? "A"
    : user?.firstName?.[0] ||
    user?.lastName?.[0] ||
    JSON.parse(localStorage.getItem("user"))?.firstName?.[0] ||
    "A";

  // Navigation handlers
  const handleSettingsClick = () => navigate('/administrative/settings');
  const handleLogout = async () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleLogoClick = () => {
    navigate("/home-dashboard");
  };

  const handleMenuClick = (itemId) => {
    if (expandedMenu === itemId) {
      setExpandedMenu("");
    } else {
      setExpandedMenu(itemId);
    }
  };

  const handleSubItemClick = (id) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    const getParentSection = (id) => {
      for (const item of menuItems) {
        if (item.subItems?.some(subItem => subItem.id === id)) {
          return item.id;
        }
      }
      return "";
    };

    const parentSection = getParentSection(id);
    if (parentSection) {
      setExpandedMenu(parentSection);

      // Special handling for different sections
      switch (parentSection) {
        case "advisor-portal":
          if (id === "clients") {
            navigate("/advisor-portal/client-database");
          } else if (id === "add-new-client") {
            setShowAddClientModal(true);
            return;
          } else {
            navigate(`/advisor-portal/${id}`);
          }
          break;

        case "financial-planning":
          navigate(`/financial-planning/${id}`);
          break;

        case "portfolio-management":
          if (id === "allocations") {
            navigate("/portfolio-management/firm-allocations");
          } else {
            navigate(`/portfolio-management/${id}`);
          }
          break;

        case "marketing-sales":
          navigate(`/marketing-sales/${id}`);
          break;

        case "education-licensing":
          navigate(`/education-licensing/${id}`);
          break;

        case "administrative":
          navigate(`/administrative/${id}`);
          break;

        default:
          navigate(`/${id}`);
      }
    } else {
      navigate(`/${id}`);
    }
  };

  // Menu items configuration
  const menuItems = [
    {
      id: "advisor-portal",
      icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
      </svg>,
      label: "Advisor Portal",
      subItems: [
        {
          id: "clients",
          label: "Client Database",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zM16 13c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
          </svg>,
        },
        {
          id: "calendar-events",
          label: "Calendar",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z" />
          </svg>,
        },
        {
          id: "events",
          label: "Events",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z" />
          </svg>,
        },
        {
          id: "messages",
          label: "Messages",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
          </svg>,
        },
        {
          id: "email",
          label: unreadCount > 0 ? `Email (${unreadCount})` : "Email",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z" />
          </svg>,
        },
        {
          id: "client-onboarding",
          label: "Client Onboarding",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>,
        },
        {
          id: "add-new-client",
          label: "Add New Client",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>,
        },
      ],
    },
    {
      id: "financial-planning",
      icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
      </svg>,
      label: "Financial Planning",
      subItems: [
        {
          id: "insurance",
          label: "Insurance",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>,
        },
        {
          id: "tax-planning",
          label: "Tax Planning",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 3H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
          </svg>,
        },
        {
          id: "estate-planning",
          label: "Estate Planning",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
          </svg>,
        },
        {
          id: "banking",
          label: "Banking",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
          </svg>,
        },
        {
          id: "financial-calculators",
          label: "Financial Calculators",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 14h-3v-3h3v3zm0-5h-3v-3h3v3zm-5 5H8v-3h3v3zm0-5H8v-3h3v3zm-5 5H4v-3h3v3zm0-5H4v-3h3v3z" />
          </svg>,
        },
        {
          id: "analysis-reports",
          label: "Analysis & Reports",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-3H7V7h10v2z" />
          </svg>,
        },
        {
          id: "retirement-journey",
          label: "Retirement Journey",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
          </svg>,
        },
      ],
    },
    {
      id: "portfolio-management",
      icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z" />
      </svg>,
      label: "Portfolio Management",
      subItems: [
        {
          id: "allocations",
          label: "Allocations",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
          </svg>,
        },
        {
          id: "market-news",
          label: "Market News",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-1 14H5V8h14v10z" />
          </svg>,
        },
        {
          id: "market-research",
          label: "Market Research",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>,
        },
        {
          id: "portfolio-reports",
          label: "Portfolio Reports",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-3H7V7h10v2z" />
          </svg>,
        },
        {
          id: "trading-rebalancing",
          label: "Trading & Rebalancing",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 8l-4 4h3c0 3.31-2.69 6-6 6-1.01 0-1.97-.25-2.8-.7l-1.46 1.46C8.97 19.54 10.43 20 12 20c4.42 0 8-3.58 8-8h3l-4-4zM6 12c0-3.31 2.69-6 6-6 1.01 0 1.97.25 2.8.7l1.46-1.46C15.03 4.46 13.57 4 12 4c-4.42 0-8 3.58-8 8H1l4 4 4-4H6z" />
          </svg>,
        },
        {
          id: "withdrawals-distributions",
          label: "Withdrawals & Distributions",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
          </svg>,
        },
        {
          id: "alternative-assets",
          label: "Alternative Assets",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 6h-2.18c.11-.31.18-.65.18-1a3 3 0 00-3-3 3 3 0 00-2.68 1.66L12 4l-.32-.34A3 3 0 009 2a3 3 0 00-3 3c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
          </svg>,
        },
        {
          id: "billing",
          label: "Billing",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2zM12 13h2v2h-2zm-4 0h2v2H8zm8 0h2v2h-2z" />
          </svg>,
        },
      ],
    },
    {
      id: "marketing-sales",
      icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
      </svg>,
      label: "Marketing & Sales",
      subItems: [
        {
          id: "sales-coach",
          label: "Sales Coach",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z" />
          </svg>,
        },
        {
          id: "campaigns",
          label: "Campaigns",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18 11v2h4v-2h-4zm-2 6.61c.96.71 2.21 1.65 3.2 2.39.4-.53.8-1.2 1.2-1.6.99-.74-2.24-1.68-3.2-2.4-.4.54-.8 1.08-1.2 1.61zM20.4 5.6c-.4-.53-.8-1.07-1.2-1.6-.99.74-2.24 1.68-3.2 2.4.4.53.8 1.07 1.2 1.6.96-.71 2.21-1.65 3.2-2.4zM4 9c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h1v4h2v-4h1l5 3V6L8 9H4zm11.5 3c0-1.33-.58-2.53-1.5-3.35v6.69c.92-.81 1.5-2.01 1.5-3.34z" />
          </svg>,
        },
        {
          id: "social-media-marketing",
          label: "Social Media",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6.61c.96.71 2.21 1.65 3.2 2.39.4-.53.8-1.2 1.2-1.6.99-.74-2.24-1.68-3.2-2.4-.4.54-.8 1.08-1.2 1.61zM20.4 5.6c-.4-.53-.8-1.07-1.2-1.6-.99.74-2.24 1.68-3.2 2.4.4.53.8 1.07 1.2 1.6.96-.71 2.21-1.65 3.2-2.4zM4 9c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h1v4h2v-4h1l5 3V6L8 9H4zm11.5 3c0-1.33-.58-2.53-1.5-3.35v6.69c.92-.81 1.5-2.01 1.5-3.34z" />
          </svg>,
        },
        {
          id: "design-hub",
          label: "Design Hub",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8z" />
          </svg>,
        },
        {
          id: "marketing-analytics",
          label: "Analytics",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-3H7V7h10v2z" />
          </svg>,
        },
      ],
    },
    {
      id: "education-licensing",
      icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" />
      </svg>,
      label: "Education & Licensing",
      subItems: [
        {
          id: "financial-literacy",
          label: "Financial Literacy",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M4 10h3v7H4zm6.5 0h3v7h-3zM2 19h20v3H2zm15-9h3v7h-3zM12 1L2 6v2h20V6L12 3z" />
          </svg>,
        },
        {
          id: "financial-news",
          label: "Financial News",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-1 14H5V8h14v10z" />
          </svg>,
        },
        {
          id: "licenses-insurance",
          label: "Licenses & Insurance",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>,
        },
        {
          id: "ce-requirements",
          label: "CE Requirements",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
          </svg>,
        },
      ],
    },
    {
      id: "administrative",
      icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
      </svg>,
      label: "Administrative",
      subItems: [
        {
          id: "firm-overview",
          label: "Firm Overview",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
          </svg>,
        },
        {
          id: "company-structure",
          label: "Company Structure",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
          </svg>,
        },
        {
          id: "branding",
          label: "Branding",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9 8 9.67 8 10.5 7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8z" />
          </svg>,
        },
        {
          id: "compliance-center",
          label: "Compliance Center",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>,
        },
        {
          id: "payroll-tax",
          label: "Payroll & Tax",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
          </svg>,
        },
        {
          id: "firm-analytics",
          label: "Firm Analytics",
          icon: <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
          </svg>,
        },
        {
          id: "firm-settings",
          label: "Firm Settings",
          icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>,
        },
      ],
    },
  ];

  const handleSectionClick = (sectionId) => {
    setExpandedMenu(sectionId);
    // Navigate to the first item in the section by default
    const section = menuItems.find(item => item.id === sectionId);
    if (section && section.subItems && section.subItems.length > 0) {
      handleSubItemClick(section.subItems[0].id);
    }
  };

  return (
    <div
      className="flex min-h-0 h-full w-full overflow-x-auto bg-gray-50 dark:bg-gray-900"
      id="app-layout-root"
      onClick={handleGlobalClick}
      onMouseDown={handleGlobalClick}
      onMouseUp={handleGlobalClick}
      onPointerDown={handleGlobalClick}
      onPointerUp={handleGlobalClick}
    >
      {/* Left Sidebar */}
      <aside
        ref={leftSidebarRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: leftSidebarCollapsed ? 'calc(3.5rem - 2px)' : 'calc(13rem - 2px)',
          zIndex: 40,
          transition: 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: isDarkMode ? '#0f172a' : 'rgb(36, 43, 99)',
        }}
        className="flex flex-col"
      >
        {/* User Profile - Smaller on mobile */}
        <div className="flex justify-center pt-2 pb-1.5 lg:pt-3 lg:pb-2">
          <div
            onClick={handleSettingsClick}
            className={`
              w-6 h-6 lg:w-7 lg:h-7 rounded-full flex items-center justify-center
              font-bold text-xs lg:text-sm cursor-pointer hover:opacity-90 transition-opacity
              ${isDarkMode 
                 ? 'bg-gray-700 text-[#00ff9d]'    // dark mode circle + green text
                 : 'bg-white text-[#202EC6]'}      // light mode circle + blue text
            `}
          >
            {advisorInitial}
          </div>
        </div>

        {/* Navigation Menu - Adjusted padding and text size */}
        <nav className="flex-1 overflow-hidden hover:overflow-y-auto">
          {menuItems.map((item) => (
            <div key={item.id}>
              <button
                onClick={() => handleMenuClick(item.id)}
                className={`
                  flex items-center justify-start w-full px-3 py-1.5 lg:py-2 text-left
                  text-white transition-colors duration-200
                  ${expandedMenu === item.id ? 'bg-[#202EC6]/50' : 'hover:bg-[#202EC6]/30'}
                `}
              >
                <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                  <div className={`transition-transform duration-200 ${leftSidebarCollapsed ? 'transform translate-x-1' : ''}`}>
                    {item.icon}
                  </div>
                </div>
                <span 
                  className={`ml-2 text-[10px] lg:text-xs whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out ${
                    leftSidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                  }`}
                >
                  {item.label}
                </span>
              </button>

              {!leftSidebarCollapsed && expandedMenu === item.id && (
                <div className="bg-[#202EC6]/20">
                  {item.subItems.map((subItem) => (
                    <button
                      key={subItem.id}
                      onClick={() => handleSubItemClick(subItem.id)}
                      className="flex items-center w-full px-4 py-1.5 text-left text-white hover:bg-[#202EC6]/30"
                    >
                      <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                        <div className={`transition-transform duration-200 ${leftSidebarCollapsed ? 'transform translate-x-1' : ''}`}>
                          {subItem.icon}
                        </div>
                      </div>
                      <span className="ml-2 text-[10px] lg:text-xs whitespace-nowrap">{subItem.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom Actions + Dark/Light Toggle - Now with mt-auto to push to bottom */}
        <div className={`mt-auto border-t space-y-1 p-1.5 ${isDarkMode ? 'border-[#00ff9d]/20' : 'border-[#131d80]'}`}>
          {/* Dark / Light mode switch */}
          <button
            onClick={toggleDarkMode}
            className={`
              flex items-center justify-start w-full px-3 py-1.5 transition-colors duration-200
              ${isDarkMode 
                 ? 'bg-white text-[#131d80] hover:bg-gray-100' 
                 : 'bg-[#131d80] text-white hover:bg-[#202EC6]/30'}
            `}
          >
            {isDarkMode 
              ? <Sun className="w-5 h-5 flex-shrink-0" /> 
              : <Moon className="w-5 h-5 flex-shrink-0" />
            }
            <span className={`ml-2 text-[10px] lg:text-xs whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out ${
              leftSidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
            }`}>
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </span>
          </button>
          <button
            onClick={handleSettingsClick}
            className="flex items-center justify-start w-full px-3 py-1.5 text-white hover:bg-[#202EC6]/30"
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            <span className={`ml-2 text-[10px] lg:text-xs whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out ${
              leftSidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
            }`}>Settings</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center justify-start w-full px-3 py-1.5 text-white hover:bg-[#202EC6]/30"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span className={`ml-2 text-[10px] lg:text-xs whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out ${
              leftSidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
            }`}>Logout</span>
          </button>
        </div>
      </aside>

      {/* Vertical Neon Line - Only in dark mode */}
      {isDarkMode && (
        <div
          className="fixed top-0 z-40 w-0.5 neon-divider-vertical"
          style={{
            left: leftSidebarCollapsed ? 'calc(3.5rem - 2px)' : 'calc(13rem - 2px)',
            height: '100vh',
            transition: 'left 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      )}

      {/* Collapse Toggle Button – pulled out so it sits above SubPageLayout */}
      <div
        className="fixed top-1/2 -translate-y-1/2 z-[1049]"
                          style={{
          left: leftSidebarCollapsed ? (isDarkMode ? 'calc(3.5rem - 2px)' : 'calc(3.5rem - 4px)') : (isDarkMode ? 'calc(13rem - 2px)' : 'calc(13rem - 4px)'),
          transform: 'translateX(-50%)',
          transition: 'left 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 50,
          ...(isDemoMode && {
            pointerEvents: 'none',
            cursor: 'not-allowed'
          })
        }}
      >
        <button
          onClick={() => setLeftSidebarCollapsed(!leftSidebarCollapsed)}
          className={`sidebar-toggle-button w-5 h-10 rounded-full flex items-center justify-center transition-colors duration-200 ${
            isDarkMode ? 'neon-toggle-button' : ''
          }`}
          style={{
            background: isDarkMode ? '#0f172a' : 'rgb(36, 43, 99)',
            border: isDarkMode ? 'none' : `1px solid rgb(36, 43, 99)`,
            ...(isDemoMode ? { pointerEvents: 'none', cursor: 'not-allowed' } : {})
          }}
        >
          {leftSidebarCollapsed 
            ? <ChevronRight className="w-3.5 h-3.5 text-white" />
            : <ChevronLeft className="w-3.5 h-3.5 text-white" />
          }
        </button>
      </div>

      {/* Main Content Area */}
      <main
        className="flex-1 flex flex-col min-h-0 relative transition-all duration-300 ease-in-out"
                  style={{
            marginTop: 'var(--header-height)',
            height: 'calc(100vh - var(--header-height))',
            marginLeft: leftSidebarCollapsed ? 'calc(3.5rem - 2px)' : 'calc(13rem - 2px)',
            width: `calc(100% - ${leftSidebarCollapsed ? 'calc(3.5rem - 2px)' : 'calc(13rem - 2px)'} - ${rightSidebarWidth}px)`,
            transition: 'margin-left 200ms cubic-bezier(0.4, 0, 0.2, 1), width 0ms',
            ...(isDemoMode && {
              cursor: 'default'  // Show default cursor instead of not-allowed to indicate scrollability
            })
          }}
      >
        {/* Header */}
        <header
          className="h-14 bg-white dark:bg-gray-800 flex flex-col fixed top-0 z-30 border-b border-gray-200 dark:border-gray-700"
          style={{
            left: leftSidebarCollapsed ? 'calc(3.5rem - 2px)' : 'calc(13rem - 2px)',
            right: `${rightSidebarWidth}px`,
            transition: 'left 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            ...(isDemoMode && {
              cursor: 'default'  // Show default cursor in header during demo
            })
          }}
          ref={measureHeader}
        >
          <div className="flex-1 flex items-center justify-between px-4">
            <div className="flex items-center">
              <img
                className="h-8 w-auto cursor-pointer"
                src={isDarkMode ? "/images/logos/q-logo-white.png" : "/images/logos/q-logo-color.png"}
                alt="Retirement Systems"
                onClick={handleLogoClick}
              />
            </div>

            {/* Navigation Links - Responsive text */}
            <div className="hidden lg:flex items-center justify-center flex-grow min-w-0 mx-4">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSectionClick(item.id)}
                  className={`
                    px-[clamp(0.25rem,0.5vw,0.75rem)]
                    text-[clamp(0.65rem,0.8vw,0.875rem)]
                    transition-all duration-200
                    ${leftSidebarCollapsed 
                      ? 'whitespace-nowrap'  // Prevent wrapping when collapsed
                      : 'min-w-[5rem] max-w-[8rem]' // Allow wrapping when expanded
                    }
                    ${expandedMenu === item.id
                      ? 'text-[#029831] dark:text-[#00ff9d]'
                      : 'text-gray-500 hover:text-gray-700 dark:text-white dark:hover:text-gray-200'
                    }`}
                >
                  <span className="block text-center" style={{ 
                    lineHeight: '1.2',
                    ...(leftSidebarCollapsed 
                      ? { whiteSpace: 'nowrap' }
                      : {
                          whiteSpace: 'break-spaces',
                          wordSpacing: '-1px'
                        })
                  }}>
                    {leftSidebarCollapsed 
                      ? item.label 
                      : item.label.replace(' & ', '\n& ')}
                  </span>
                </button>
              ))}
            </div>

            {/* Search Bar - Responsive sizing */}
            <div className="flex items-center">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <Search className="h-3 w-3 lg:h-4 lg:w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  className={`pl-6 pr-3 py-1.5
                           ${leftSidebarCollapsed ? 'w-36' : 'w-[clamp(8rem,12vw,12rem)]'}
                           text-[clamp(0.65rem,0.8vw,0.875rem)] rounded-md
                           border border-gray-300 dark:border-gray-600 
                           focus:outline-none focus:ring-2 focus:ring-[#029831] focus:border-[#029831] 
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           transition-all duration-200`}
                />
              </div>
            </div>
          </div>

          {/* Divider bar - Now part of the header */}
          {isDarkMode ? (
            <div className="relative">
              <div 
                className="h-[2px] neon-divider" 
                style={{
                  marginLeft: leftSidebarCollapsed ? 'calc(-3.5rem)' : 'calc(-13rem)',
                  width: `calc(100% + ${leftSidebarCollapsed ? '3.5rem' : '13rem'})`,
                }}
              />
            </div>
          ) : (
            <div className="h-[2px] bg-[#029831] w-full" />
          )}
        </header>

        {/* Page Content */}
        <div
          className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-gray-900"
          style={{
            width: '100%',
            height: 'calc(100vh - var(--header-height))',
            ...(isDemoMode && {
              cursor: 'default'  // Show default cursor to indicate scrollability
            })
          }}
        >
          <Outlet />
        </div>
      </main>

      {/* Add Client Modal */}
      {showAddClientModal && (
        <AddClientModal onClose={() => setShowAddClientModal(false)} />
      )}
    </div>
  );
}

export default function AppLayout() {
  return (
    <LayoutProvider>
      <AppLayoutContent />
    </LayoutProvider>
  );
} 